import React, { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../services/api';
import { useNavigate } from 'react-router-dom';

export default function ManageTrips() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [duplicateNameByTripId, setDuplicateNameByTripId] = useState({});
  const [deleteConfirmByTripId, setDeleteConfirmByTripId] = useState({});
  const [error, setError] = useState('');
  const [busyTripId, setBusyTripId] = useState(null);

  const tripsById = useMemo(() => {
    const map = {};
    trips.forEach(t => { map[t.trip_id] = t; });
    return map;
  }, [trips]);

  const refreshTrips = async () => {
    setError('');
    const res = await fetch(apiUrl('/api/trips'));
    const data = await res.json();
    setTrips(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    refreshTrips();
  }, []);

  const duplicateTrip = async (tripId) => {
    const name = (duplicateNameByTripId[tripId] || '').trim();
    if (!name) return;

    setBusyTripId(tripId);
    setError('');
    try {
      const res = await fetch(apiUrl(`/api/trips/${tripId}/duplicate`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trip_name: name })
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(payload.error || 'Failed to duplicate trip');
        return;
      }
      setDuplicateNameByTripId(prev => ({ ...prev, [tripId]: '' }));
      await refreshTrips();
    } finally {
      setBusyTripId(null);
    }
  };

  const deleteTrip = async (tripId) => {
    const trip = tripsById[tripId];
    if (!trip) return;

    const typed = (deleteConfirmByTripId[tripId] || '').trim();
    if (typed !== trip.trip_name) return;

    const ok = window.confirm(`Delete "${trip.trip_name}" and ALL its destinations/activities? This cannot be undone.`);
    if (!ok) return;

    setBusyTripId(tripId);
    setError('');
    try {
      const res = await fetch(apiUrl(`/api/trips/${tripId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_name: typed })
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        setError(payload.error || 'Failed to delete trip');
        return;
      }
      setDeleteConfirmByTripId(prev => ({ ...prev, [tripId]: '' }));
      await refreshTrips();
    } finally {
      setBusyTripId(null);
    }
  };

  return (
    <div className="App">
      <section className="tp-hero">
        <p className="tp-hero__kicker">Admin</p>
        <h1 className="tp-hero__title">Manage Trips</h1>
        <p className="tp-hero__subtitle">
          Duplicate a trip (including destinations and activities) or permanently delete one.
        </p>
        <div className="tp-row" style={{ marginTop: 14 }}>
          <button className="tp-btn--ghost" onClick={() => navigate('/')}>Back to Home</button>
          <button className="tp-btn--ghost" onClick={refreshTrips}>Refresh</button>
        </div>
      </section>

      {error && (
        <section className="tp-section">
          <div className="tp-card" style={{ borderColor: 'rgba(255,92,92,0.35)' }}>
            <div style={{ color: 'var(--tp-danger)' }}>{error}</div>
          </div>
        </section>
      )}

      <section className="tp-section">
        <div className="tp-card">
          <div className="tp-row" style={{ justifyContent: 'space-between' }}>
            <h2 className="tp-section__title" style={{ margin: 0 }}>Trips</h2>
            <span className="tp-pill">Count: {trips.length}</span>
          </div>

          {trips.length === 0 ? (
            <p style={{ color: 'var(--tp-muted)', marginTop: 12 }}>No trips found.</p>
          ) : (
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Created</th>
                    <th>Duplicate</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map(trip => {
                    const busy = busyTripId === trip.trip_id;
                    const typed = (deleteConfirmByTripId[trip.trip_id] || '');
                    const canDelete = typed.trim() === trip.trip_name;

                    return (
                      <tr key={trip.trip_id}>
                        <td>{trip.trip_name}</td>
                        <td>{trip.created_at}</td>
                        <td>
                          <div className="tp-row">
                            <input
                              type="text"
                              placeholder={`Copy name (must be unique)`}
                              value={duplicateNameByTripId[trip.trip_id] || ''}
                              onChange={(e) => setDuplicateNameByTripId(prev => ({ ...prev, [trip.trip_id]: e.target.value }))}
                              disabled={busy}
                            />
                            <button
                              onClick={() => duplicateTrip(trip.trip_id)}
                              disabled={busy || !(duplicateNameByTripId[trip.trip_id] || '').trim()}
                            >
                              Duplicate
                            </button>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ color: 'var(--tp-muted)', fontSize: 12 }}>
                              Type <b>{trip.trip_name}</b> to confirm.
                            </div>
                            <div className="tp-row">
                              <input
                                type="text"
                                placeholder="Enter exact trip name"
                                value={typed}
                                onChange={(e) => setDeleteConfirmByTripId(prev => ({ ...prev, [trip.trip_id]: e.target.value }))}
                                disabled={busy}
                              />
                              <button
                                className="tp-btn--danger"
                                onClick={() => deleteTrip(trip.trip_id)}
                                disabled={busy || !canDelete}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
